import { Navigate, useLocation } from 'react-router';

/**
 * Redirects legacy /new/* paths to /* since v2 now lives at root.
 * This handles the transition from when helios-ui was served at /new.
 */
export const LegacyNewRedirect = () => {
    const location = useLocation();
    // Remove /new prefix and redirect to the same path at root
    const newPath = location.pathname.replace(/^\/new/, '') || '/';
    const newLocation = `${newPath}${location.search}${location.hash}`;
    return <Navigate to={newLocation} replace />;
};

/**
 * Parses legacy v1 hash-based routes and returns the equivalent helios-ui path.
 * These routes were used in old email links and bookmarks.
 *
 * Examples:
 *   #sites/123 -> /site/123/dashboard/overview
 *   #sites/123/performance/1month -> /site/123/dashboard/performance?interval=LAST_MONTH
 *   #company/123 -> /companies/123
 *   #companies -> /companies
 *   #home -> /
 *   #account -> /settings
 */
const parseLegacyHash = (hash: string): string | null => {
    if (!hash || !hash.startsWith('#')) return null;

    const hashParts = hash.split('/');
    const hashRoute = hashParts[0];

    switch (hashRoute) {
        case '#add-company':
            return '/companies/create';

        case '#add-site':
            return '/sites/create';

        case '#companies':
            return '/companies';

        case '#company': {
            const companyId = parseInt(hashParts[1]);
            if (isNaN(companyId)) return '/';

            switch (hashParts[2]) {
                case 'risk': {
                    const overrideId = parseInt(hashParts[3]);
                    return isNaN(overrideId)
                        ? `/companies/${companyId}/alert-settings`
                        : `/companies/${companyId}/alert-settings/overrides`;
                }
                case 'events':
                    return `/companies/${companyId}/logs`;
                case 'cpe':
                    return `/companies/${companyId}/devices`;
                default:
                    return `/companies/${companyId}`;
            }
        }

        case '#home':
            switch (hashParts[1]) {
                case 'events':
                    return '/logs';
                default:
                    return '/';
            }

        case '#sites': {
            const siteId = parseInt(hashParts[1]);
            if (isNaN(siteId)) return '/';

            switch (hashParts[2]) {
                case 'performance': {
                    const range = hashParts[3];
                    const params =
                        range === '1month' ? '?interval=LAST_MONTH' : '';
                    return `/site/${siteId}/dashboard/performance${params}`;
                }
                case 'troubleshoot':
                    return `/site/${siteId}/dashboard/diagnostics`;
                case 'config':
                    return `/site/${siteId}/configuration`;
                case 'events':
                    return `/site/${siteId}/dashboard/logs`;
                default:
                    return `/site/${siteId}/dashboard/overview`;
            }
        }

        case '#account':
            switch (hashParts[1]) {
                case 'events':
                    return '/logs';
                default:
                    return '/settings';
            }

        default:
            // Unknown hash route, don't redirect
            return null;
    }
};

export const LegacyHashRedirect = () => {
    const location = useLocation();
    const newPath = parseLegacyHash(location.hash);

    if (newPath) {
        window.location.replace(newPath);
        return null;
    }

    return null;
};
